import { doctorNameEncoder, URL_QUERY } from '../../Common/Utility/common-utils';
import { dataLayerSendEvent, sendViewItemListEvent } from '../../Common/Utility/analytics-utils';
import { TELECONSULT_ROUTE } from '../../Common/Utility/constants';
import { sendEventToSmartLook } from './smartlook';
import { getAttributeItemFromArray } from '../../Common/Utility/common-utils';
import { getAge } from '@asterCore/Profile/AutoScrollForm/utils';

const sendUserEvent = async (userInfo, isLogin) => {
  const userLogin = 'user_logged_in';
  const userUpdate = 'user_details_update';
  const registrationDate = userInfo?.extension?.find((x) => x?.url === 'patientRegistrationDate')?.valueString;

  const loginData = {
    login: true,
    registration_date: registrationDate,
  };
  if (isLogin) {
    dataLayerSendEvent(userLogin, loginData);
    sendEventToSmartLook('login_success', loginData);
  }
  dataLayerSendEvent(userUpdate, {
    user_id: userInfo?.id,
    age: getAge(userInfo?.birthDate),
    nationality: userInfo?.extension?.find((x) => x?.url === 'nationality')?.valueCoding?.display,
    gender: userInfo?.gender,
    email: userInfo?.telecom?.find((x) => x?.system === 'email')?.value,
  });
};

export const updateUserData = (userData, isLogin = false) => {
  sendUserEvent(userData, isLogin);
};

export const updateListingData = (screenName, listingData, type) => {
  let listings = [];
  if (type === 'clinics') {
    listings = listingData?.map((list) => getClinicListings(list));
  }
  if (type === 'doctor') {
    listings = listingData?.map((list) => getDoctorListings(list));
  }
};

export const getClinicListings = (clinicData) => {
  const { id, name, image, facilityImageWeb, facilityType, uriPath } = clinicData;
  const taxonomy = ['Teleconsultation', facilityType];
  const url = `${location?.origin}${TELECONSULT_ROUTE}/f/${uriPath?.replace(URL_QUERY, '')}/${id}?type=all`;
  const productImageUrl = facilityImageWeb?.length > 0 ? facilityImageWeb[0] : image;
  return {
    ...getProductMainAttributes({ id, name, taxonomy, url, productImageUrl }),
    ...getCustomProductAttributes({ mainCategory: facilityType }, 'Teleconsultation'),
  };
};

export const getClinicDetailsAttributes = (clinicdata) => {
  const id = clinicdata?.clinicId;
  const name = clinicdata?.facilityName;
  const taxonomy = ['Teleconsultation', clinicdata?.facility];
  const url = `${location?.origin}${TELECONSULT_ROUTE}/f/${clinicdata?.facilityUriPath?.replace(URL_QUERY, '')}/${
    clinicdata?.clinicId
  }?type=all`;
  const productImageUrl = clinicdata?.facilityImageWeb?.profileImageData[0]?.facilityImageAttributes?.url;
  return {
    ...getProductMainAttributes({ id, name, taxonomy, url, productImageUrl }),
    ...getCustomProductAttributes({ mainCategory: clinicdata?.facility }, 'Teleconsultation'),
  };
};

export const getDoctorListings = (doctorData) => {
  const { id, name, specialization, profileURL } = doctorData;
  const taxonomy = ['Teleconsultation', specialization];
  const url = `${location?.origin}${TELECONSULT_ROUTE}/d/${doctorNameEncoder(name).replace(URL_QUERY, '')}/${id}`;
  return {
    ...getProductMainAttributes({ id, name, taxonomy, url, productImageUrl: profileURL }),
    ...getCustomProductAttributes({ mainCategory: specialization }, 'Teleconsultation'),
  };
};

export const getAppointmentAttributes = (appointmentData) => {
  const { doctorName, doctorImage, doctorSpecialty, paymentAmount, doctorId, payerType, points } = appointmentData;
  let amount = parseFloat(Number(paymentAmount).toFixed(2));
  amount = isNaN(amount) ? 0 : amount;
  const id = doctorId;
  const name = doctorName;
  const taxonomy = ['Teleconsultation', doctorSpecialty];
  const url = `${location?.origin}${TELECONSULT_ROUTE}/d/${doctorNameEncoder(doctorName)?.replace(
    URL_QUERY,
    '',
  )}/${doctorId}`;
  const productImageUrl = doctorImage;
  return {
    quantity: 1,
    subtotal: amount,
    ...getProductMainAttributes({ id, name, taxonomy, url, productImageUrl, unitPrice: amount, unitSalePrice: amount }),
    ...getCustomProductAttributes(
      { mainCategory: doctorSpecialty, payment: payerType, loyaltyPoints: points },
      'Teleconsultation',
    ),
  };
};

export const getTeleconsultDetailsAttributes = ({ data: doctorData, vcpId }) => {
  const id = vcpId;
  const name = doctorData?.fullName;
  const taxonomy = ['Teleconsultation', doctorData?.primarySpeciality];
  const url = `${location?.origin}${TELECONSULT_ROUTE}/d/${doctorNameEncoder(doctorData?.fullName)?.replace(
    URL_QUERY,
    '',
  )}/${vcpId}`;
  const productImageUrl = doctorData?.profileImageWeb?.profileImageData?.profileImageAttributes?.url;
  return {
    item_id: doctorData?.hisDoctorId,
    item_name: doctorData?.fullName,
    coupon: '',
    currency: 'AED',
    discount: '',
    index: '',
    item_brand: '',
    item_category: doctorData?.primarySpeciality,
    item_list_id: doctorData?.facilityList?.[0]?.facilityId,
    item_list_name: doctorData?.facilityList?.[0]?.facilityName,
    price: 0,
    quantity: 1,
    ...getProductMainAttributes({ id, name, taxonomy, url, productImageUrl }),
    ...getCustomProductAttributes({ mainCategory: doctorData?.primarySpeciality }, 'Teleconsultation'),
  };
};

const getProductBrand = (productData) => {
  return productData?.brandDetails?.brandName || productData?.brand || productData?.item_brand || getAttributeItemFromArray(productData?.customAttribute, "brandName") || productData?.brand_details?.brandName;
};

const getProductExpressValue = (productData) => {
  return productData?.express || productData?.express_bool;
};

const getProductCategory = (categoryDetails) => {
  let mainCategory = '';
  let subCategory = '';
  if (categoryDetails?.length > 5) {
    const categoryNewDetails = categoryDetails.slice(0, 5);
    mainCategory = categoryNewDetails[categoryNewDetails.length - 2]?.categoryName;
    subCategory = categoryNewDetails[categoryNewDetails.length - 1]?.categoryName;
  }
  if (categoryDetails?.length <= 5) {
    mainCategory = categoryDetails[categoryDetails.length - 2]?.categoryName;
    subCategory = categoryDetails[categoryDetails.length - 1]?.categoryName;
  }

  return {
    mainCategory,
    subCategory,
  };
};

export const getProductListingAttributes = (productData, type = '') => {
  let mainCategory = '';
  let subCategory = '';
  const brand = getProductBrand(productData);
  const express = getProductExpressValue(productData);

  if (productData) {
    const category = getProductCategory(productData.categoryDetails);
    mainCategory = category.mainCategory;
    subCategory = category.subCategory;
  }

  return getCustomProductAttributes(
    {
      brand,
      express,
      mainCategory,
      subCategory,
    },
    type,
  );
};

export const getProductDetailsAttributes = (productData, type = '') => {
  let mainCategory = '';
  let subCategory = '';

  const brand = getProductBrand(productData);
  const express = getProductExpressValue(productData);

  if (productData) {
    const category = getProductCategory(productData.categoryDetails);
    mainCategory = category.mainCategory;
    subCategory = category.subCategory;

    if (!productData.categoryDetails && productData.taxonomy?.length > 0) {
      mainCategory = productData.taxonomy[productData.taxonomy?.length - 2];
      subCategory = productData.taxonomy[productData.taxonomy?.length - 1];
    }
  }

  return getCustomProductAttributes(
    {
      brand,
      express,
      mainCategory,
      subCategory,
    },
    type,
  );
};

export const getProductDetailAttributes = (productData, type = '') => {
  const express = getProductExpressValue(productData);

  return {
      express_availability: express? 'true' : 'false',
    };
};

const getProductMainAttributes = ({
  id,
  name,
  taxonomy,
  currency = 'AED',
  url,
  unitPrice = 0,
  unitSalePrice = 0,
  stock = 1,
  productImageUrl,
}) => {
  return {
    id,
    name,
    taxonomy,
    currency,
    url,
    unit_price: unitPrice,
    unit_sale_price: unitSalePrice,
    stock,
    product_image_url: productImageUrl,
  };
};

const getCustomProductAttributes = (
  { brand = '', express = false, loyaltyPoints = 0, mainCategory = '', subCategory = '', payment = '' },
  type,
) => {
  return {
    brand_name: brand,
    express_availability: express,
    loyalty_points_earned: loyaltyPoints,
    main_category_name: mainCategory,
    sub_category_name: subCategory,
    payment_selected: payment,
    product_selected: type,
  };
};

